// Media cache — the Cache API side of the atomic-swap flow (docs/04 §Running).
//
// The player plays STRICTLY from cache: media is resolved to blob object-URLs
// read out of the Cache API, so playback is identical online and offline and
// does not depend on the Service Worker being active (important for tests and
// for the first seconds after a cold boot). Downloads are sequential — one item
// at a time — so a weak uplink is never saturated. A new manifest version is
// only ever swapped in once ALL of its media is cached.

import type { Config } from './config.ts';
import { apiUrl } from './config.ts';
import type { Manifest } from './types.ts';
import { diffMedia, isFullyCached, mediaUrlsOf } from './cacheplan.ts';
import { estimateStorage, preflight } from './quota.ts';
import { loadMediaLedger, saveMediaLedger } from './storage.ts';

const MEDIA_CACHE = 'sl-player-media-v1';

/** True for the DOMException the Cache API throws when the origin quota is full. */
function isQuotaError(e: unknown): boolean {
  return e instanceof DOMException && (e.name === 'QuotaExceededError' || e.code === 22);
}

export class MediaCache {
  /** Resolved blob object-URLs, keyed by manifest media URL. Revoked on prune. */
  private objectUrls = new Map<string, string>();
  /** Raised when a swap was refused for lack of storage; cleared on a clean swap. */
  private pressure = false;

  /**
   * @param getToken supplies the device token so downloads of the token-authed
   *   `/v1/player/media/:id` route carry `Authorization: Bearer`. Relative
   *   (same-origin/API-base) media only — external type=url pages are never
   *   fetched here, and mock `/mock/*` assets simply ignore the header.
   */
  constructor(
    private cfg: Config,
    private getToken: () => string | null = () => null,
    private log: (code: string, message?: string) => void = () => {},
  ) {}

  /** Whether the last swap attempt was refused for lack of storage (heartbeat). */
  storagePressure(): boolean {
    return this.pressure;
  }

  private authHeaders(mediaUrl: string): HeadersInit | undefined {
    if (/^https?:/i.test(mediaUrl)) return undefined; // external: no token
    const token = this.getToken();
    return token ? { authorization: `Bearer ${token}` } : undefined;
  }

  private open(): Promise<Cache> {
    return caches.open(MEDIA_CACHE);
  }

  /** URLs currently present in the cache (keys are absolute request URLs). */
  private async cachedUrls(): Promise<string[]> {
    const cache = await this.open();
    const reqs = await cache.keys();
    return reqs.map((r) => this.toManifestUrl(r.url));
  }

  /** Cache keys are absolute; manifest URLs are relative. Normalise for diffing. */
  private toManifestUrl(absolute: string): string {
    const base = this.cfg.apiBase || location.origin;
    try {
      const u = new URL(absolute);
      const b = new URL(base, location.origin);
      if (u.origin === b.origin) return u.pathname + u.search;
    } catch {
      /* fall through */
    }
    return absolute;
  }

  private fetchUrl(mediaUrl: string): string {
    // Relative manifest URL → absolute against the API base.
    return /^https?:/i.test(mediaUrl) ? mediaUrl : apiUrl(this.cfg, mediaUrl);
  }

  /**
   * Ensure every media item of `manifest` is cached. Downloads missing items
   * ONE AT A TIME. Resolves true only when the manifest is fully cached (safe to
   * swap); resolves false if any download failed (keep the old manifest).
   */
  async ensureCached(manifest: Manifest): Promise<boolean> {
    const cache = await this.open();
    const needed = mediaUrlsOf(manifest);
    const cached = await this.cachedUrls();
    const { toDownload } = diffMedia(cached, needed);
    if (toDownload.length === 0) {
      this.pressure = false; // already fully cached → no pressure
      return isFullyCached(manifest, cached);
    }

    // Pre-flight: predict the footprint of what we're about to download from the
    // per-URL byte ledger and refuse early if it clearly won't fit (keeps the old
    // manifest playing). Unknown URLs contribute 0, so a first-time swap still
    // proceeds and relies on the write-time QuotaExceededError backstop below.
    const ledger = await loadMediaLedger();
    const report = await estimateStorage();
    const pf = preflight(toDownload, ledger, report);
    if (!pf.fits) {
      this.pressure = true;
      this.log(
        'storage_preflight_refused',
        `need ~${pf.knownBytes}B (+${pf.unknownCount} unknown) > ${pf.available}B free`,
      );
      return false;
    }

    let ledgerDirty = false;
    for (const mediaUrl of toDownload) {
      const fetchUrl = this.fetchUrl(mediaUrl);
      let res: Response;
      try {
        const init: RequestInit = { cache: 'no-store' };
        const headers = this.authHeaders(mediaUrl);
        if (headers) init.headers = headers;
        res = await fetch(fetchUrl, init);
        if (!res.ok) return false;
      } catch {
        return false; // network died mid-download → abort, retry next poll
      }
      try {
        await cache.put(fetchUrl, res.clone());
      } catch (e) {
        if (isQuotaError(e)) {
          // The definitive quota refusal: keep the old manifest, raise pressure.
          this.pressure = true;
          this.log('storage_quota_exceeded', mediaUrl);
          if (ledgerDirty) await saveMediaLedger(ledger);
          return false;
        }
        return false;
      }
      try {
        ledger[mediaUrl] = (await res.blob()).size;
        ledgerDirty = true;
      } catch {
        /* size accounting is best-effort — a missing entry just stays "unknown" */
      }
    }
    if (ledgerDirty) await saveMediaLedger(ledger);
    const ok = isFullyCached(manifest, await this.cachedUrls());
    if (ok) this.pressure = false; // a clean, complete swap clears pressure
    return ok;
  }

  /**
   * Remove cache entries (and revoke object-URLs) not referenced by `manifest`.
   * Call only AFTER a successful swap so in-flight playback never loses its media.
   */
  async prune(manifest: Manifest): Promise<void> {
    const cache = await this.open();
    const needed = new Set(mediaUrlsOf(manifest));
    const reqs = await cache.keys();
    for (const req of reqs) {
      const mUrl = this.toManifestUrl(req.url);
      if (!needed.has(mUrl)) {
        await cache.delete(req);
        const obj = this.objectUrls.get(mUrl);
        if (obj) {
          URL.revokeObjectURL(obj);
          this.objectUrls.delete(mUrl);
        }
      }
    }
  }

  /**
   * A playable src for a media item. External (type=url) URLs are returned as-is
   * (rendered live in an iframe). Cached image/video resolve to a blob object-URL
   * so the element never touches the network. Returns null on a cache miss.
   */
  async resolveSrc(mediaUrl: string): Promise<string | null> {
    if (/^https?:/i.test(mediaUrl)) return mediaUrl;
    const existing = this.objectUrls.get(mediaUrl);
    if (existing) return existing;
    const cache = await this.open();
    const res = await cache.match(this.fetchUrl(mediaUrl));
    if (!res) return null;
    const blob = await res.blob();
    const obj = URL.createObjectURL(blob);
    this.objectUrls.set(mediaUrl, obj);
    return obj;
  }

  /** Drop every cached media entry and revoke object-URLs (factory reset). */
  async clear(): Promise<void> {
    for (const obj of this.objectUrls.values()) URL.revokeObjectURL(obj);
    this.objectUrls.clear();
    await caches.delete(MEDIA_CACHE);
  }
}
